/**
 * PNG read and write, with no dependencies.
 *
 * Writing is deliberately deterministic. The same pixels always produce the
 * same bytes: fixed deflate level, no timestamp chunk, no text chunk, and the
 * None filter on every scanline rather than a heuristic that picks per row.
 * That costs a little file size on a 32x32 sprite and buys the ability to ask
 * "did anything change?" and have git answer truthfully.
 *
 * Reading exists so a spec can be diffed against art that already ships.
 */

import { deflateSync, inflateSync } from "node:zlib";

export interface Bitmap {
	width: number;
	height: number;
	/** RGBA, 4 bytes per pixel, row major. */
	data: Uint8Array;
}

const SIGNATURE = Uint8Array.from([137, 80, 78, 71, 13, 10, 26, 10]);

const CRC_TABLE = (() => {
	const t = new Int32Array(256);
	for (let n = 0; n < 256; n++) {
		let c = n;
		for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
		t[n] = c;
	}
	return t;
})();

function crc32(bytes: Uint8Array): number {
	let c = -1;
	for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
	return (c ^ -1) >>> 0;
}

function u32(value: number): Uint8Array {
	return Uint8Array.from([(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff]);
}

function concat(parts: Uint8Array[]): Uint8Array {
	const total = parts.reduce((n, p) => n + p.length, 0);
	const out = new Uint8Array(total);
	let at = 0;
	for (const p of parts) {
		out.set(p, at);
		at += p.length;
	}
	return out;
}

function chunk(type: string, body: Uint8Array): Uint8Array {
	const tag = Uint8Array.from([...type].map((ch) => ch.charCodeAt(0)));
	const payload = concat([tag, body]);
	return concat([u32(body.length), payload, u32(crc32(payload))]);
}

export function encodePng(bmp: Bitmap): Uint8Array {
	const stride = bmp.width * 4;
	// One extra byte per row for the filter type, which is always 0 (None).
	const raw = new Uint8Array((stride + 1) * bmp.height);
	for (let y = 0; y < bmp.height; y++) {
		raw[y * (stride + 1)] = 0;
		raw.set(bmp.data.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1);
	}
	const ihdr = concat([
		u32(bmp.width),
		u32(bmp.height),
		Uint8Array.from([8, 6, 0, 0, 0]), // depth 8, colour type 6 (RGBA), no interlace
	]);
	return concat([
		SIGNATURE,
		chunk("IHDR", ihdr),
		chunk("IDAT", new Uint8Array(deflateSync(raw, { level: 9 }))),
		chunk("IEND", new Uint8Array(0)),
	]);
}

const CHANNELS: Record<number, number> = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 };

function paeth(a: number, b: number, c: number): number {
	const p = a + b - c;
	const pa = Math.abs(p - a);
	const pb = Math.abs(p - b);
	const pc = Math.abs(p - c);
	if (pa <= pb && pa <= pc) return a;
	return pb <= pc ? b : c;
}

export function decodePng(bytes: Uint8Array): Bitmap {
	for (let i = 0; i < SIGNATURE.length; i++) {
		if (bytes[i] !== SIGNATURE[i]) throw new Error("not a PNG");
	}
	const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

	let width = 0;
	let height = 0;
	let depth = 0;
	let colourType = 0;
	let palette: Uint8Array | null = null;
	let transparency: Uint8Array | null = null;
	const idat: Uint8Array[] = [];

	let at = 8;
	while (at < bytes.length) {
		const length = view.getUint32(at);
		const type = String.fromCharCode(bytes[at + 4], bytes[at + 5], bytes[at + 6], bytes[at + 7]);
		const body = bytes.subarray(at + 8, at + 8 + length);
		if (type === "IHDR") {
			width = view.getUint32(at + 8);
			height = view.getUint32(at + 12);
			depth = body[8];
			colourType = body[9];
			if (body[12] !== 0) throw new Error("interlaced PNGs are not supported");
		} else if (type === "PLTE") {
			palette = body.slice();
		} else if (type === "tRNS") {
			transparency = body.slice();
		} else if (type === "IDAT") {
			idat.push(body.slice());
		} else if (type === "IEND") {
			break;
		}
		at += 12 + length;
	}
	if (depth !== 8) throw new Error(`only 8-bit PNGs are supported, this one is ${depth}-bit`);
	const channels = CHANNELS[colourType];
	if (!channels) throw new Error(`unsupported PNG colour type ${colourType}`);

	const raw = new Uint8Array(inflateSync(concat(idat)));
	const stride = width * channels;
	const pixels = new Uint8Array(stride * height);

	// Undo the per-scanline filter. Each row may reference the row above it, so
	// this has to run in order.
	for (let y = 0; y < height; y++) {
		const filter = raw[y * (stride + 1)];
		const src = raw.subarray(y * (stride + 1) + 1, (y + 1) * (stride + 1));
		const row = pixels.subarray(y * stride, (y + 1) * stride);
		const prev = y > 0 ? pixels.subarray((y - 1) * stride, y * stride) : null;
		for (let i = 0; i < stride; i++) {
			const left = i >= channels ? row[i - channels] : 0;
			const up = prev ? prev[i] : 0;
			const upLeft = prev && i >= channels ? prev[i - channels] : 0;
			let value = src[i];
			if (filter === 1) value += left;
			else if (filter === 2) value += up;
			else if (filter === 3) value += (left + up) >> 1;
			else if (filter === 4) value += paeth(left, up, upLeft);
			else if (filter !== 0) throw new Error(`unknown PNG filter ${filter}`);
			row[i] = value & 0xff;
		}
	}

	const out = new Uint8Array(width * height * 4);
	for (let i = 0; i < width * height; i++) {
		const s = i * channels;
		const d = i * 4;
		if (colourType === 6) {
			out.set(pixels.subarray(s, s + 4), d);
		} else if (colourType === 2) {
			out.set(pixels.subarray(s, s + 3), d);
			out[d + 3] = 255;
		} else if (colourType === 0) {
			out[d] = out[d + 1] = out[d + 2] = pixels[s];
			out[d + 3] = 255;
		} else if (colourType === 4) {
			out[d] = out[d + 1] = out[d + 2] = pixels[s];
			out[d + 3] = pixels[s + 1];
		} else {
			const idx = pixels[s];
			if (!palette) throw new Error("indexed PNG with no palette");
			out[d] = palette[idx * 3];
			out[d + 1] = palette[idx * 3 + 1];
			out[d + 2] = palette[idx * 3 + 2];
			out[d + 3] = transparency && idx < transparency.length ? transparency[idx] : 255;
		}
	}
	return { width, height, data: out };
}
